import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import apiClient from '../services/api/config';
import { pushNotificationService } from '../services/pushNotificationService';
import type { PermissionState } from '../services/pushNotificationService';

export interface NotificationCounts {
  announcements: number;
  policies: number;
  learning: number;
  games: number;
  rewards: number;
  pulse: number;
  tasks: number;
  documents: number;
  calendar: number;
  recognition: number;
  total: number;
}

interface NotificationContextType {
  counts: NotificationCounts;
  refreshCounts: () => Promise<void>;
  markAsRead: (module: keyof NotificationCounts, count?: number) => void;
  isLoading: boolean;
  /** Push notification helpers */
  pushPermission: PermissionState;
  isPushSubscribed: boolean;
  subscribeToPush: () => Promise<boolean>;
  unsubscribeFromPush: () => Promise<boolean>;
  /** Whether the permission prompt banner should be shown */
  showPermissionPrompt: boolean;
  dismissPermissionPrompt: () => void;
}

const defaultCounts: NotificationCounts = {
  announcements: 0,
  policies: 0,
  learning: 0,
  games: 0,
  rewards: 0,
  pulse: 0,
  tasks: 0,
  documents: 0,
  calendar: 0,
  recognition: 0,
  total: 0,
};

const PROMPT_DISMISSED_KEY = 'moreme_push_prompt_dismissed';

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [counts, setCounts] = useState<NotificationCounts>(defaultCounts);
  const [isLoading, setIsLoading] = useState(false);
  const [pushPermission, setPushPermission] = useState<PermissionState>(
    pushNotificationService.getPermission()
  );
  const [isPushSubscribed, setIsPushSubscribed] = useState(false);
  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);
  const prevTotalRef = useRef(0);

  // ─── Push initialisation (runs once on mount when logged in) ──
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (!pushNotificationService.isSupported()) return;

    const perm = pushNotificationService.getPermission();
    setPushPermission(perm);

    if (perm === 'granted') {
      // Already granted – silently subscribe (idempotent)
      pushNotificationService.subscribe().then(() => {
        pushNotificationService.isSubscribed().then(setIsPushSubscribed);
      });
    } else if (perm === 'default') {
      // Show prompt banner (unless user previously dismissed it)
      const dismissed = localStorage.getItem(PROMPT_DISMISSED_KEY);
      if (!dismissed) {
        setShowPermissionPrompt(true);
      }
    }
  }, []);

  const subscribeToPush = useCallback(async () => {
    const ok = await pushNotificationService.subscribe();
    setPushPermission(pushNotificationService.getPermission());
    setIsPushSubscribed(ok);
    setShowPermissionPrompt(false);
    return ok;
  }, []);

  const unsubscribeFromPush = useCallback(async () => {
    const ok = await pushNotificationService.unsubscribe();
    setIsPushSubscribed(!ok);
    return ok;
  }, []);

  const dismissPermissionPrompt = useCallback(() => {
    setShowPermissionPrompt(false);
    localStorage.setItem(PROMPT_DISMISSED_KEY, '1');
  }, []);

  // ─── Notification counts ──────────────────────────────────────

  const refreshCounts = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch notification counts from API
      const response = await apiClient.get('/notifications/counts');
      if (response.data?.success && response.data?.data) {
        const data = response.data.data;
        const newCounts: NotificationCounts = {
          announcements: data.announcements || 0,
          policies: data.policies || 0,
          learning: data.learning || 0,
          games: data.games || 0,
          rewards: data.rewards || 0,
          pulse: data.pulse || data.chat || 0,
          tasks: data.tasks || 0,
          documents: data.documents || 0,
          calendar: data.calendar || 0,
          recognition: data.recognition || 0,
          total: 0,
        };
        // Calculate total
        newCounts.total = Object.values(newCounts).reduce((sum, val) => sum + val, 0);
        setCounts(newCounts);
      }
    } catch (error) {
      // If API fails, try to get counts from individual endpoints
      try {
        const [announcementsRes, policiesRes, learningRes] = await Promise.all([
          apiClient.get('/announcements?unread=true').catch(() => ({ data: { data: [] } })),
          apiClient.get('/policies?unread=true').catch(() => ({ data: { data: [] } })),
          apiClient.get('/learning/new').catch(() => ({ data: { data: [] } })),
        ]);

        const newCounts: NotificationCounts = {
          announcements: Array.isArray(announcementsRes.data?.data) ? announcementsRes.data.data.filter((a: any) => !a.isRead).length : 0,
          policies: Array.isArray(policiesRes.data?.data) ? policiesRes.data.data.filter((p: any) => !p.isRead).length : 0,
          learning: Array.isArray(learningRes.data?.data) ? learningRes.data.data.length : 0,
          games: 0,
          rewards: 0,
          pulse: 0,
          tasks: 0,
          documents: 0,
          calendar: 0,
          recognition: 0,
          total: 0,
        };
        newCounts.total = Object.values(newCounts).reduce((sum, val) => sum + val, 0);
        setCounts(newCounts);
      } catch {
        // Silently fail - keep default counts
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ─── Badge + push notification on count changes ───────────────

  useEffect(() => {
    // Update the app badge whenever total count changes
    pushNotificationService.setBadge(counts.total);

    // If count went UP compared to previous, fire a local push notification
    if (counts.total > prevTotalRef.current && prevTotalRef.current > 0) {
      const diff = counts.total - prevTotalRef.current;
      pushNotificationService.showNotification({
        title: 'MoreMe AI',
        body: `You have ${diff} new item${diff > 1 ? 's' : ''} waiting for you!`,
        tag: 'moreme-count-update',
        url: '/app/dashboard',
        type: 'general',
        badgeCount: counts.total,
      });
    }
    prevTotalRef.current = counts.total;
  }, [counts.total]);

  const markAsRead = useCallback((module: keyof NotificationCounts, count = 1) => {
    setCounts(prev => {
      const newCount = Math.max(0, prev[module] - count);
      const newTotal = prev.total - (prev[module] - newCount);
      return {
        ...prev,
        [module]: newCount,
        total: Math.max(0, newTotal),
      };
    });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    if (window.location.pathname === '/questionnaire') return;
    refreshCounts();
    const interval = setInterval(() => {
      const t = localStorage.getItem('token');
      if (t && window.location.pathname !== '/questionnaire') refreshCounts();
    }, 60000);
    return () => clearInterval(interval);
  }, [refreshCounts]);

  const value: NotificationContextType = {
    counts,
    refreshCounts,
    markAsRead,
    isLoading,
    pushPermission,
    isPushSubscribed,
    subscribeToPush,
    unsubscribeFromPush,
    showPermissionPrompt,
    dismissPermissionPrompt,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
